import { ChangeEvent, KeyboardEvent, useRef } from 'react';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import CircularProgress from '@mui/material/CircularProgress';
import {
  deleteConversationUpload,
  listConversationUploads,
  uploadConversationUpload,
} from '../../services/chatService';
import { ChatUploadSummary } from '../../interfaces/ChatInterface';
import useErrorModal from '../hooks/useErrorModal';
import useChatUploads from '../hooks/useChatUploads';
import { ACCEPTED_EXTENSIONS_ATTR, formatFileSize, MAX_UPLOAD_COUNT, validateUploadFile } from './chatUploads';
import ChatFileChip from './ChatFileChip';
import FileIcon from '../assets/file-icon.svg';
import CloseIcon from '../assets/close-icon.svg';

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: (attachments: ChatUploadSummary[]) => void;
  eoaId: string;
  accessToken: string;
  scope: string;
  disabled?: boolean;
  placeholder?: string;
  canGenerateSummary?: boolean;
  isSummarizing?: boolean;
  onGenerateSummary?: () => void;
  // Uploads already carried by a sent message; hidden here so they leave the composer on send.
  sentUploadIds?: ReadonlySet<string>;
  // While a response streams, the send button shows a spinner and a Stop button appears.
  isGenerating?: boolean;
  onStop?: () => void;
}

const ChatInput = ({
  value,
  onChange,
  onSend,
  eoaId,
  accessToken,
  scope,
  disabled,
  placeholder,
  canGenerateSummary,
  isSummarizing,
  onGenerateSummary,
  sentUploadIds,
  isGenerating,
  onStop,
}: ChatInputProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { handleOpen: handleOpenErrorModal } = useErrorModal();

  const { uploads, uploading, uploadFiles, removeUpload } = useChatUploads({
    reloadKey: `${eoaId}:${scope}`,
    maxCount: MAX_UPLOAD_COUNT,
    list: () => listConversationUploads(eoaId, accessToken, scope),
    upload: (file: File) => uploadConversationUpload(eoaId, accessToken, scope, file),
    remove: (uploadId: string) => deleteConversationUpload(eoaId, accessToken, uploadId),
    loadErrorMessage: 'Failed to load attachments',
    deleteErrorMessage: 'Failed to remove attachment',
  });

  const pendingUploads = uploads.filter((upload) => !sentUploadIds?.has(upload.id));
  const isUploading = uploading.length > 0;

  const handleSend = () => onSend(pendingUploads);

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!disabled && !isUploading && value.trim()) handleSend();
    }
  };

  const handleFilesSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    const valid: File[] = [];
    files.forEach((file) => {
      const error = validateUploadFile(file);
      if (error) {
        handleOpenErrorModal(error, new Error(error));
      } else {
        valid.push(file);
      }
    });
    if (valid.length > 0) void uploadFiles(valid);
  };

  const hasAttachments = pendingUploads.length > 0 || uploading.length > 0;

  return (
    <div className="chat-input">
      {hasAttachments && (
        <ul className="chat-input-attachments">
          {pendingUploads.map((upload) => (
            <li key={upload.id} className="chat-input-attachment">
              <ChatFileChip
                icon={<img src={FileIcon} alt="" className="chat-file-chip-icon" />}
                name={upload.original_file_name}
                secondary={formatFileSize(upload.size_bytes)}
                action={
                  <button
                    type="button"
                    className="chat-file-chip-remove"
                    aria-label={`Remove ${upload.original_file_name}`}
                    onClick={() => void removeUpload(upload.id)}
                  >
                    <img src={CloseIcon} alt="" />
                  </button>
                }
              />
            </li>
          ))}
          {uploading.map((item) => (
            <li key={item.id} className="chat-input-attachment chat-input-attachment-uploading">
              <ChatFileChip
                icon={<img src={FileIcon} alt="" className="chat-file-chip-icon" />}
                name={item.name}
                secondary="Uploading…"
              />
            </li>
          ))}
        </ul>
      )}

      <textarea
        className="chat-input-textarea"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={handleKeyDown}
        rows={1}
        placeholder={placeholder ?? 'Type here or select a prompt'}
      />

      <div className="chat-input-actions">
        <div className="chat-input-actions-left">
          <input
            ref={fileInputRef}
            type="file"
            className="chat-input-file"
            accept={ACCEPTED_EXTENSIONS_ATTR}
            multiple
            onChange={handleFilesSelected}
          />
          <button
            type="button"
            className="chat-input-attach"
            aria-label="Attach files"
            onClick={() => fileInputRef.current?.click()}
          >
            <AttachFileIcon fontSize="small" />
          </button>
          {canGenerateSummary && (
            <button
              type="button"
              className="chat-input-generate-summary"
              onClick={onGenerateSummary}
              disabled={disabled || isSummarizing || isUploading}
            >
              {isSummarizing ? 'Generating summary…' : 'Generate summary'}
            </button>
          )}
        </div>
        <div className="chat-input-actions-right">
          {isGenerating && (
            <button type="button" className="chat-input-stop" aria-label="Stop generating" onClick={onStop}>
              <StopRoundedIcon fontSize="small" />
            </button>
          )}
          <button
            type="button"
            className="chat-input-send"
            aria-label="Send message"
            onClick={handleSend}
            disabled={disabled || !value.trim() || isUploading}
          >
            {isGenerating ? (
              <CircularProgress color="inherit" size={16} thickness={5} />
            ) : (
              <ArrowUpwardIcon fontSize="small" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatInput;
